/**
 * International Auctions — the partner auction house page (BXS-3627 F1.2).
 *
 * Route: `/:locale/international-auctions/:house`
 *
 * THIS IS THE PAGE WE SEND A PARTNER A LINK TO. It is a sales artefact before
 * it is a browse surface, so everything above the grid is co-branded from the
 * house record itself — `brand_color` behind the band, `brand_color_contrast`
 * as its ink, the house's own logo on a white plaque. Nothing here hardcodes a
 * partner's colour: Tattersalls navy, Arqana claret and Fasig-Tipton blue all
 * come from data, reach CSS as the `--house-brand` custom property on a real
 * element, and a house we have never heard of gets the same treatment for free.
 *
 * Three behaviours worth knowing before changing anything:
 *
 *   1. LOADING IS A REAL STATE. The service answers on a simulated 250 ms, and
 *      the house lookup and the sales lookup are separate round trips. Neither
 *      the not-found state nor the "no sales" state may render while its own
 *      request is still in flight — that flash is the classic bug in this
 *      pattern, so `houseLoading` and `salesLoading` are tracked separately and
 *      each gates only its own region.
 *
 *   2. THE BLURB HAS NO `@if`. Arqana ships an English description and no
 *      Arabic one; `resolveTranslation()` falls back locale → en → any, and the
 *      Arabic site must therefore show the English paragraph rather than a hole.
 *      Hiding an empty blurb would hide exactly the case the fixture exists to
 *      prove.
 *
 *   3. THE STATUS FILTER LIVES IN THE URL. `?status=live` is the single source
 *      of truth, so the filtered view is shareable with a partner and the back
 *      button walks the filter history. The house itself is fixed by the route,
 *      so there is no house filter here — unlike the index.
 *
 * Models:  ../_mock/international-auctions.models.ts
 * Service: ../../../_services/international-auctions.service.ts
 */
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { LocaleLinkPipe } from 'src/app/Shared/_pipes/locale-link/locale-link.pipe';
import { LocaleService } from 'src/app/Shared/services/locale/locale.service';

import { InternationalAuctionsService } from '../../../_services/international-auctions.service';
import {
  ExternalHouse,
  ExternalSaleStatus,
  ExternalSaleView,
  SupportedLocale,
  resolveTranslation,
} from '../_mock/international-auctions.models';
import { HouseBrand, resolveHouseBrand } from '../_shared/house-brand';
import { InternationalSaleCardComponent } from '../_shared/sale-card/international-sale-card.component';

/** One pill in the status row. `null` is "All" — the absence of a filter. */
interface StatusFilterOption {
  readonly value: ExternalSaleStatus | null;
  readonly labelKey: string;
}

/**
 * The pills, in the mockup's order. Spelled out rather than derived from the
 * `ExternalSaleStatus` union so the keys stay greppable for a translation
 * extraction sweep, exactly as the status chip does it.
 */
const STATUS_FILTERS: readonly StatusFilterOption[] = [
  { value: null, labelKey: 'international_auctions.filters.all' },
  { value: 'live', labelKey: 'international_auctions.status.live' },
  { value: 'upcoming', labelKey: 'international_auctions.status.upcoming' },
  { value: 'preview', labelKey: 'international_auctions.status.preview' },
  { value: 'closed', labelKey: 'international_auctions.status.closed' },
];

/** Guards `?status=` — anything else is treated as no filter at all. */
const KNOWN_STATUSES: readonly ExternalSaleStatus[] = [
  'live',
  'upcoming',
  'preview',
  'closed',
];

/** Placeholder cards shown while the sales request is in flight. */
const SKELETON_CARDS: readonly number[] = [0, 1, 2];

@Component({
  selector: 'app-international-auctions-house',
  standalone: true,
  imports: [
    RouterLink,
    TranslatePipe,
    LocaleLinkPipe,
    InternationalSaleCardComponent,
  ],
  templateUrl: './international-auctions-house.component.html',
  styleUrl: './international-auctions-house.component.scss',
})
export class InternationalAuctionsHouseComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly auctions = inject(InternationalAuctionsService);
  private readonly localeService = inject(LocaleService);
  private readonly cdr = inject(ChangeDetectorRef);

  /** The route's `:house` value, kept for the not-found copy and debugging. */
  houseSlug = '';

  /** `null` once loaded means the slug is unknown — never an error state. */
  house: ExternalHouse | null = null;

  /** Keyed on the house object itself — see the `brand` getter. */
  private brandKey: ExternalHouse | null = null;
  private brandValue: HouseBrand = resolveHouseBrand(null);
  sales: ExternalSaleView[] = [];

  /** Separate flags: the header must not wait on the grid, or vice versa. */
  houseLoading = true;
  salesLoading = true;

  /** Mirrors `?status=`; `null` is the "All" pill. */
  activeStatus: ExternalSaleStatus | null = null;

  locale: SupportedLocale = 'en';

  readonly statusFilters = STATUS_FILTERS;
  readonly skeletonCards = SKELETON_CARDS;

  /**
   * A stable array reference — a fresh literal in the template would re-run
   * `RouterLink.ngOnChanges` on every change-detection pass.
   */
  readonly indexLink: readonly string[] = ['/international-auctions'];

  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    // Subscribed rather than read once: switching language must re-resolve the
    // blurb, the country and the venue without a page reload.
    this.subscriptions.add(
      this.localeService.languageCode$.subscribe((code) => {
        this.locale = code === 'ar' ? 'ar' : 'en';
        this.cdr.markForCheck();
      })
    );

    const slug$ = this.route.paramMap.pipe(
      map((params) => (params.get('house') ?? '').trim()),
      distinctUntilChanged()
    );

    // The house identity. Refetched only when the slug itself changes, so
    // flipping a status pill never blanks the co-branded header.
    this.subscriptions.add(
      slug$
        .pipe(
          tap((slug) => {
            this.houseSlug = slug;
            this.house = null;
            this.houseLoading = true;
            this.cdr.markForCheck();
          }),
          // `switchMap` so a slow answer for an abandoned slug can never
          // overwrite the answer for the slug we are actually on.
          switchMap((slug) => this.auctions.getHouse(slug))
        )
        .subscribe((house) => {
          this.house = house;
          this.houseLoading = false;
          this.cdr.markForCheck();
        })
    );

    // The sales. Driven by slug + `?status=` together, so a shared link with a
    // filter on it renders filtered on first paint, server-side included.
    this.subscriptions.add(
      combineLatest([
        slug$,
        this.route.queryParamMap.pipe(
          map((params) => this.readStatus(params.get('status')))
        ),
      ])
        .pipe(
          distinctUntilChanged(
            ([slugA, statusA], [slugB, statusB]) =>
              slugA === slugB && statusA === statusB
          ),
          tap(([, status]) => {
            // Set before the request resolves: the pill highlights instantly,
            // which is what makes the 250 ms feel like a filter and not a wait.
            this.activeStatus = status;
            this.salesLoading = true;
            this.cdr.markForCheck();
          }),
          switchMap(([slug, status]) =>
            this.auctions.listSalesByHouse(slug, status)
          )
        )
        .subscribe((sales) => {
          // Already ordered live → upcoming → preview → closed by the service.
          // Re-sorting here would silently fork the ordering from the index.
          this.sales = sales;
          this.salesLoading = false;
          this.cdr.markForCheck();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /* --- Filtering ---------------------------------------------------------- */

  isStatusActive(value: ExternalSaleStatus | null): boolean {
    return this.activeStatus === value;
  }

  /**
   * Writes the pill into the URL rather than into component state. `null`
   * removes the parameter entirely, so "All" reads as a clean `/tattersalls`
   * — the URL a partner would want to be sent.
   */
  selectStatus(value: ExternalSaleStatus | null): void {
    if (this.activeStatus === value) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { status: value },
      queryParamsHandling: 'merge',
    });
  }

  /* --- House ------------------------------------------------------------- */

  /**
   * The band's palette, resolved once per house rather than per change
   * detection — four `[style.--*]` bindings read it and a fresh object every
   * pass would dirty all four.
   */
  get brand(): HouseBrand {
    if (this.house !== this.brandKey) {
      this.brandKey = this.house;
      this.brandValue = resolveHouseBrand(this.house);
    }

    return this.brandValue;
  }

  /** Still read by the sale cards below the band, which take a single colour. */
  get brandColor(): string {
    return this.brand.color;
  }

  get brandContrast(): string {
    return this.brand.ink;
  }

  get houseName(): string {
    return this.house?.name ?? '';
  }

  /**
   * The location line as the house publishes it ("Newmarket, UK"), falling back
   * to the bare country code rather than to nothing.
   */
  get countryLabel(): string {
    if (!this.house) {
      return '';
    }
    return (
      resolveTranslation(this.house.country_name, this.locale) ??
      this.house.country_code ??
      ''
    );
  }

  get venueLabel(): string {
    return resolveTranslation(this.house?.base_venue, this.locale) ?? '';
  }

  /**
   * NO `@if` guards this in the template. Arqana supplies English only, and the
   * whole point of `resolveTranslation`'s fallback chain is that the Arabic
   * site then shows the English paragraph instead of an empty block.
   */
  get houseDescription(): string {
    return resolveTranslation(this.house?.description, this.locale) ?? '';
  }

  get websiteUrl(): string {
    return (this.house?.website_url ?? '').trim();
  }

  /** Read through a getter rather than off `house` directly, so the template
   *  never leans on control-flow narrowing of a mutable class property. */
  get logoUrl(): string {
    return (this.house?.logo_url ?? '').trim();
  }

  get hasLogo(): boolean {
    return this.logoUrl.length > 0;
  }

  /* --- Sales -------------------------------------------------------------- */

  /**
   * ngx-translate has no plural rule of its own, so the two keys are chosen
   * here. `0` takes the `_other` form, which is correct in both en and ar.
   */
  get salesCountKey(): string {
    return this.sales.length === 1
      ? 'international_auctions.house.sales_count_one'
      : 'international_auctions.house.sales_count_other';
  }

  get hasSales(): boolean {
    return this.sales.length > 0;
  }

  /* --- Internals ---------------------------------------------------------- */

  /**
   * An unrecognised `?status=` reads as no filter rather than as an error, and
   * the URL is deliberately left alone: rewriting it would fight the back
   * button, which is the one thing this parameter exists to preserve.
   */
  private readStatus(raw: string | null): ExternalSaleStatus | null {
    const value = (raw ?? '').trim().toLowerCase();
    const match = KNOWN_STATUSES.find((status) => status === value);
    return match ?? null;
  }
}
